import { normalizeUnitOfMeasure, quantityAllowsFraction } from '@shared/quantity';
import type { BulkPreviewItem, ProductMutationOpts } from '../domain/catalogTypes';
import { getSessionOrganizationId, requireSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { getCatalogRamSnapshotCache, patchWebCatalogProductInCache } from './catalogRamSnapshotCache';
import { appendProductCatalogEvent } from './productCatalogEventWriter';
import { submitCommand } from '../../sync/core/submitCommand';
import { matchesSearchAny } from '../../shared/format/searchText';
import {
  categoryMatchesBulkScope,
  bulkCategoryScopeForAudit,
  type BulkCategoryScope,
} from '../application/bulkCategoryScope';
import {
  computeBulkPriceAfterCoefficient,
  DEFAULT_BULK_PRICE_ROUNDING,
  type BulkPriceRounding,
} from '../application/bulkPriceRounding';
import { getOrgMoneyScaleFromStore } from '../../shared/money/orgMoneyScale';
import { BULK_COEFFICIENT_PREVIEW_FETCH_LIMIT } from '../application/bulkCoefficientConstants';

type RamProductRow = {
  id?: unknown;
  organization_id?: unknown;
  is_misc?: unknown;
  item_type?: unknown;
  is_active?: unknown;
  category_id?: unknown;
  name?: unknown;
  sku?: unknown;
  barcode?: unknown;
  price?: unknown;
  unit_of_measure?: unknown;
};

type RamStockRow = { product_id?: unknown; branch_id?: unknown; quantity?: unknown };

function sortBulkScopeRows(rows: RamProductRow[]): RamProductRow[] {
  return [...rows].sort((a, b) => {
    const byName = String(a.name ?? '').localeCompare(String(b.name ?? ''), 'es', { sensitivity: 'base' });
    if (byName !== 0) return byName;
    return String(a.id ?? '').localeCompare(String(b.id ?? ''));
  });
}

/**
 * Alcance compartido del bulk: productos físicos, activos, no-misc de la org,
 * filtrados por categoría (incluir / excluir) y búsqueda (nombre/SKU/código de barras).
 */
function filterBulkScopeRows(
  products: RamProductRow[],
  organizationId: string,
  categoryScope: BulkCategoryScope,
  search: string,
): RamProductRow[] {
  return products
    .filter((p) => String(p.organization_id ?? '') === organizationId)
    .filter((p) => p.is_misc !== true && p.item_type !== 'service' && p.is_active !== false)
    .filter((p) => categoryMatchesBulkScope(p.category_id != null ? String(p.category_id) : null, categoryScope))
    .filter((p) =>
      matchesSearchAny(
        [p.name, p.sku, p.barcode].map((v) => (v == null ? null : String(v))),
        search,
      ),
    );
}

export type BulkScopeCounts = {
  /** Ítems del alcance con precio cargado (> 0): son los que toca el coeficiente. */
  priced: number;
  /** Ítems del alcance sin precio (0 o vacío): quedan igual con cualquier %. */
  unpriced: number;
};

export type BulkPreviewLoadResult = {
  items: BulkPreviewItem[];
  /** Productos que se aplicarían al confirmar (precios: solo con precio > 0). */
  applyTotal: number;
};

type BulkScopeQuery = {
  categoryScope?: BulkCategoryScope;
  search?: string;
  branchId?: string | null;
  coefficient: number;
  applyTo: 'prices' | 'quantities';
  previewLimit?: number;
  rounding?: BulkPriceRounding;
};

function resolveScopedSortedRows(
  organizationId: string,
  categoryScope: BulkCategoryScope,
  search: string,
): RamProductRow[] {
  const products = getCatalogRamSnapshotCache()?.products ?? [];
  return sortBulkScopeRows(filterBulkScopeRows(products, organizationId, categoryScope, search));
}

function rowsForApply(scoped: RamProductRow[], applyTo: 'prices' | 'quantities'): RamProductRow[] {
  if (applyTo === 'prices') return scoped.filter((row) => Number(row.price ?? 0) > 0);
  return scoped;
}

function mapPreviewItems(
  rows: RamProductRow[],
  applyTo: 'prices' | 'quantities',
  branchId: string | null | undefined,
  coefficient: number,
  rounding: BulkPriceRounding,
  moneyScale: ReturnType<typeof getOrgMoneyScaleFromStore>,
  stocks: RamStockRow[],
): BulkPreviewItem[] {
  if (applyTo === 'prices') {
    return rows.map((row) => {
      const current = Number(row.price ?? 0);
      return {
        id: String(row.id),
        name: String(row.name ?? ''),
        currentValue: current,
        newValue: computeBulkPriceAfterCoefficient(current, coefficient, rounding, moneyScale),
      };
    });
  }
  if (applyTo === 'quantities' && branchId) {
    return rows.map((row) => {
      const stock = stocks.find((s: RamStockRow) => s.product_id === row.id && s.branch_id === branchId);
      const quantity = Number(stock?.quantity ?? 0);
      const unitOfMeasure = normalizeUnitOfMeasure(row.unit_of_measure);
      const rawNewValue = quantity * coefficient;
      const newValue = quantityAllowsFraction(unitOfMeasure) ? rawNewValue : Math.round(rawNewValue);
      return {
        id: String(row.id),
        name: String(row.name ?? ''),
        currentValue: quantity,
        newValue,
      };
    });
  }
  return [];
}

/**
 * Conteo del alcance para copy honesto («480 con precio · 12 sin precio»).
 * Sin límite: es un conteo sobre el snapshot RAM, no la página de la vista previa.
 */
export const getBulkScopeCounts = async (params: {
  categoryScope?: BulkCategoryScope;
  search?: string;
}): Promise<BulkScopeCounts | null> => {
  const { categoryScope = { mode: 'all' }, search = '' } = params;
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) return null;
  const scoped = resolveScopedSortedRows(organizationId, categoryScope, search);
  let priced = 0;
  for (const row of scoped) {
    if (Number(row.price ?? 0) > 0) priced += 1;
  }
  return { priced, unpriced: scoped.length - priced };
};

export const getBulkApplyProductIds = async (params: {
  categoryScope?: BulkCategoryScope;
  search?: string;
  applyTo: 'prices' | 'quantities';
}): Promise<string[]> => {
  const { categoryScope = { mode: 'all' }, search = '', applyTo } = params;
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) return [];
  const scoped = resolveScopedSortedRows(organizationId, categoryScope, search);
  return rowsForApply(scoped, applyTo).map((row) => String(row.id));
};

export const getBulkPreview = async (params: BulkScopeQuery): Promise<BulkPreviewLoadResult> => {
  const {
    categoryScope = { mode: 'all' },
    search = '',
    branchId,
    coefficient,
    applyTo,
    previewLimit = BULK_COEFFICIENT_PREVIEW_FETCH_LIMIT,
    rounding = DEFAULT_BULK_PRICE_ROUNDING,
  } = params;
  const moneyScale = getOrgMoneyScaleFromStore();
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) return { items: [], applyTotal: 0 };

  const stocks = getCatalogRamSnapshotCache()?.stocks ?? [];
  const scoped = resolveScopedSortedRows(organizationId, categoryScope, search);
  const applyRows = rowsForApply(scoped, applyTo);
  const previewRows = applyRows.slice(0, previewLimit);
  const items = mapPreviewItems(previewRows, applyTo, branchId, coefficient, rounding, moneyScale, stocks);
  return { items, applyTotal: applyRows.length };
};

export const listDistinctProductBrandsLocal = async (): Promise<{ id: string; name: string }[]> => {
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) return [];
  const brands = getCatalogRamSnapshotCache()?.brands ?? [];
  return brands
    .filter((b) => b.id && b.name && String(b.organization_id ?? '') === organizationId)
    .filter((b) => b.is_active !== false && !b.deleted_at)
    .map((b) => ({ id: String(b.id), name: String(b.name ?? '') }))
    .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
};

export type BulkUpdateProgress = { done: number; total: number };

export const bulkUpdatePricesByCoefficient = async (
  productIds: string[],
  coefficient: number,
  opts?: ProductMutationOpts & {
    rounding?: BulkPriceRounding;
    categoryScope?: BulkCategoryScope;
    onProgress?: (progress: BulkUpdateProgress) => void;
  },
): Promise<number> => {
  const rounding = opts?.rounding ?? DEFAULT_BULK_PRICE_ROUNDING;
  const moneyScale = getOrgMoneyScaleFromStore();
  if (productIds.length === 0) return 0;
  const organizationId = await requireSessionOrganizationId();
  const timestamp = new Date().toISOString();
  const scopeAudit = opts?.categoryScope ? bulkCategoryScopeForAudit(opts.categoryScope) : undefined;
  let updated = 0;
  const total = productIds.length;
  for (const id of productIds) {
    const cached = getCatalogRamSnapshotCache()?.products.find((p) => String(p.id) === id);
    if (!cached) continue;
    const row = {
      price: Number(cached.price ?? 0),
      name: String(cached.name ?? ''),
      sku: String(cached.sku ?? ''),
      barcode: cached.barcode != null ? String(cached.barcode) : null,
      category_id: cached.category_id != null ? String(cached.category_id) : null,
      organization_id: cached.organization_id != null ? String(cached.organization_id) : null,
      item_type: cached.item_type === 'service' ? 'service' : 'physical',
      is_misc: cached.is_misc ? 1 : 0,
      is_active: cached.is_active !== false ? 1 : 0,
    };
    if (row.organization_id && row.organization_id !== organizationId) continue;
    if (row.is_misc === 1 || row.item_type === 'service') continue;
    if (row.is_active === 0) continue;
    if (row.price <= 0) continue;
    const newPrice = computeBulkPriceAfterCoefficient(row.price, coefficient, rounding, moneyScale);

    const result = await submitCommand({
      entity: 'product',
      operation: 'update',
      payload: {
        id,
        organizationId,
        name: row.name,
        price: newPrice,
        sku: row.sku,
        barcode: row.barcode ?? null,
        categoryId: row.category_id,
        updatedAt: timestamp,
        isActive: row.is_active !== 0,
        itemType: row.item_type === 'service' ? 'service' : 'physical',
        isMisc: row.is_misc === 1,
      },
    });
    if (!result.applied) {
      captureOpsFailure({
        module: 'catalog',
        op: 'catalog.bulk_price',
        error: new Error(result.ingestError?.trim() || 'bulk_price_not_applied'),
        ingestError: result.ingestError,
        applied: false,
        path: 'web-ram',
        itemId: id,
        extra: { productId: id },
      });
      opts?.onProgress?.({ done: updated, total });
      continue;
    }
    patchWebCatalogProductInCache(id, { price: newPrice, updated_at: timestamp });

    await appendProductCatalogEvent({
      productId: id,
      eventType: 'bulk_price',
      summary: `Precio ${row.price} → ${newPrice} ARS (coef. ${coefficient})`,
      payload: {
        beforePrice: row.price,
        afterPrice: newPrice,
        coefficient,
        rounding,
        ...(scopeAudit ? { categoryScope: scopeAudit } : {}),
      },
      performedByUserId: opts?.performedByUserId,
    });
    updated += 1;
    opts?.onProgress?.({ done: updated, total });
  }
  return updated;
};
